import React, { useState, useEffect } from 'react';
import {
  HStack,
  Text,
  VStack,
  Box,
  Wrap,
  WrapItem,
  Button,
} from '@chakra-ui/react';

import CharacterSlider from './TraitSlider/CharacterSlider';
import { useCharacter } from './Contexts/CharacterInfo.js';
import { useAction } from './Contexts/CharacterActions.js';
import { useGeneralContext } from '../GeneralContext.js';
import CharacterProfileInfo from './CharacterProfileInfo';
import LockButton from './LockButton.jsx';
import { TraitsDictionary } from './Contexts/ConstantsCollection.js';
import VitalityContent from './Vitality/VitalityContent.jsx';
import GearContent from './Gear/GearContent.jsx';

const CharacterContent = () => {
  const defaultName = 'Character Page';
  const {
    character,
    updateIsImperial,
    updateCurrentResilience,
    updateTotalResilience,
    updateCurrentHealth,
    updateTotalHealth,
    loaded,
  } = useCharacter();
  const { updateTraitsFromParsedTraits } = useAction();
  const { rules, fetchRules } = useGeneralContext();
  const [pageHeader, setPageHeader] = useState(defaultName);
  const [isLocked, setIsLocked] = useState(false);
  const [missingName, setMissingName] = useState(false);
  const [luckPoints, setLuckPoints] = useState(character.luckPoints || []);

  const { pointsBelow6, pointsAbove5 } = Object.values(character.traits).reduce(
    (acc, value) => {
      acc.pointsBelow6 += value < 6 ? Math.abs(value - 6) : 0;
      acc.pointsAbove5 += value > 5 ? Math.abs(value - 5) : 0;
      character.usedPoints = acc.pointsAbove5 + acc.pointsBelow6;
      return acc;
    },
    { pointsBelow6: 0, pointsAbove5: 0, totalPoints: 0 }
  );

  character.totalHealth = pointsAbove5 * 10 + 20;
  character.totalResilience = pointsBelow6 + 2;

  useEffect(() => {
    updateTotalHealth(character.totalHealth);
    updateCurrentHealth(
      character.previousSave
        ? Math.min(character.currentHealth, character.totalHealth)
        : character.totalHealth
    );
    updateTotalResilience(character.totalResilience);
    updateCurrentResilience(
      character.previousSave
        ? Math.min(character.currentResilience, character.totalResilience)
        : character.totalResilience
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [character.totalHealth, character.totalResilience]);

  useEffect(() => {
    fetchRules();
    if (rules)
      updateTraitsFromParsedTraits(
        rules.find(item => item?.title === 'Traits')
      );

    setPageHeader(character?.name === '' ? defaultName : character.name);
    if (isLocked && character?.name === '') window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [character, fetchRules, rules, updateTraitsFromParsedTraits]);

  useEffect(() => {
    setIsLocked(character.previousSave);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  return (
    <VStack spacing={10}>
      <div
        style={{
          height: '75px',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#f0f0f0',
          overflow: 'hidden',
        }}
      >
        <Text
          fontSize={{ base: '1xl', md: '2xl' }}
          fontWeight="bold"
          margin={{ base: 15, md: 25 }}
          maxWidth={{ base: '75px', md: '300px' }}
          maxHeight="75px"
          noOfLines={2}
        >
          {pageHeader}
        </Text>
        <div />
        <HStack padding="0 5px">
          <VStack spacing={0}>
            <Text>
              {character.usedPoints}/{character.availablePoints}
            </Text>
            <b>Points</b>
          </VStack>
          <Button
            fontSize={15}
            fontWeight={'bold'}
            onClick={() => {
              updateIsImperial(!character.isImperial);
            }}
          >
            Kg/Lbs
          </Button>
          <LockButton
            onChangeLocked={val => setIsLocked(val)}
            onChangePageHeader={val => setPageHeader(val)}
            onMissingName={val => setMissingName(val)}
          />
        </HStack>
      </div>
      <CharacterProfileInfo
        locked={isLocked}
        imperial={character.isImperial}
        missingName={missingName}
      />
      <Wrap spacing={0} align="center" justify="center" width={'100%'}>
        <WrapItem width="40%" minWidth="200px" marginLeft="5%" marginRight="5%">
          <CharacterSlider
            trait={TraitsDictionary['charm']}
            locked={isLocked}
            onLuckPoint={() => setLuckPoints(luckPoints + 1)}
          />
        </WrapItem>
        <WrapItem width="40%" minWidth="200px" marginLeft="5%" marginRight="5%">
          <CharacterSlider
            trait={TraitsDictionary['athletics']}
            locked={isLocked}
            onLuckPoint={() => setLuckPoints(luckPoints + 1)}
          />
        </WrapItem>
        <WrapItem width="40%" minWidth="200px" marginLeft="5%" marginRight="5%">
          <CharacterSlider
            trait={TraitsDictionary['tough']}
            locked={isLocked}
            onLuckPoint={() => setLuckPoints(luckPoints + 1)}
          />
        </WrapItem>
        <WrapItem width="40%" minWidth="200px" marginLeft="5%" marginRight="5%">
          <CharacterSlider
            trait={TraitsDictionary['sharp']}
            locked={isLocked}
            onLuckPoint={() => setLuckPoints(luckPoints + 1)}
          />
        </WrapItem>
        <WrapItem width="40%" minWidth="200px" marginLeft="5%" marginRight="5%">
          <CharacterSlider
            trait={TraitsDictionary['belief']}
            locked={isLocked}
            onLuckPoint={() => setLuckPoints(luckPoints + 1)}
          />
        </WrapItem>
      </Wrap>
      <VitalityContent luckPoints={luckPoints} />
      <GearContent />
      <Box height={100}></Box>
    </VStack>
  );
};

export default CharacterContent;
